<template>
  <div class="hyrox-score-shell">
    <!-- Back button -->
    <v-btn style="position: fixed; top: 15px; z-index: 99; left: 15px" icon large @click="goBack" class="back-btn">
      <v-icon color="white">mdi-chevron-left</v-icon>
    </v-btn>

    <v-container class="hyrox-score-container py-12 px-4">
      <div v-if="loading" class="text-center mt-12">
        <v-progress-circular indeterminate color="#fbc02d" size="64"></v-progress-circular>
        <div class="grey--text text--lighten-1 mt-4 subtitle-1">Lade Event-Details...</div>
      </div>

      <div v-else-if="isClosed" class="text-center py-12 closed-container">
        <v-icon size="80" color="#f50057" class="mb-4">mdi-calendar-remove</v-icon>
        <h1 class="text-h4 font-weight-black white--text mb-3">ANMELDUNG GESCHLOSSEN</h1>
        <p class="grey--text text--lighten-1 body-1 max-width-text mx-auto">
          Die Anmeldefrist für das HYROX Score Event am 19. Juli ist am {{ formatDeadlineDate(deadlineDate) || "11. Juli" }} abgelaufen. Eine
          Registrierung ist leider nicht mehr möglich.
        </p>
        <v-btn color="#fbc02d" class="black--text font-weight-bold mt-6 rounded-lg" large @click="goBack"> Zurück zur Startseite </v-btn>
      </div>

      <div v-else-if="isSoldOut" class="text-center py-12 closed-container">
        <v-icon size="80" color="#f50057" class="mb-4">mdi-ticket-percent</v-icon>
        <h1 class="text-h4 font-weight-black white--text mb-3">AUSVERKAUFT</h1>
        <p class="grey--text text--lighten-1 body-1 max-width-text mx-auto">
          Alle Startplätze für das HYROX Score Event am 19. Juli in St. Gallen West sind bereits vergeben.
        </p>
        <div class="warning-box pa-4 rounded-lg my-6 max-width-text mx-auto">
          Eventuell wird das Kontingent in Kürze erweitert. Schau später nochmals vorbei!
        </div>
        <v-btn color="#fbc02d" class="black--text font-weight-bold mt-2 rounded-lg" large @click="goBack"> Zurück zur Startseite </v-btn>
      </div>

      <v-card v-else class="hyrox-score-card rounded-xl elevation-24" flat>
        <div class="card-glow"></div>
        <div class="pa-6 pa-sm-8 relative-content">
          <!-- Event Header -->
          <div class="text-center mb-6">
            <span class="event-tag">19. JULI 2026</span>
            <h1 class="event-headline mt-2 text-uppercase">HYROX Score Event</h1>
            <h2 class="event-subheadline text-uppercase yellow--text text--darken-2">St. Gallen West</h2>
            <div class="divider mx-auto my-3"></div>
          </div>

          <v-window v-model="step" touchless>
            <!-- STEP 1: Info & Form Selector -->
            <v-window-item :value="1">
              <!-- Premium Info Block -->
              <div class="premium-info-block mb-8 text-left">
                <!-- Title / Pitch -->
                <div class="d-flex align-center mb-4">
                  <span class="yellow-accent-bar mr-3"></span>
                  <div>
                    <h3 class="white--text font-weight-black text-h6 text-uppercase tracking-wide mb-0" style="line-height: 1.2">Der HYROX Score</h3>
                    <div class="text-subtitle-2 yellow--text text--darken-2 font-weight-bold" style="letter-spacing: 0.5px">
                      Deine Fitness auf einen Blick
                    </div>
                  </div>
                </div>

                <p class="grey--text text--lighten-2 body-2 mb-6 leading-relaxed">
                  Der HYROX Score misst deine Leistungsfähigkeit anhand eines standardisierten Fitness-Tests. Ausdauer, Kraftausdauer und funktionelle
                  Fitness werden kombiniert und in einem Gesamtscore zusammengeführt.
                </p>

                <!-- Disciplines List -->
                <div class="disciplines-container mb-6">
                  <div
                    class="disciplines-title text-uppercase font-weight-black caption grey--text mb-3 tracking-widest"
                    style="font-size: 11px !important">
                    Die 6 Event-Disziplinen
                  </div>
                  <div class="disciplines-list d-flex flex-column gap-2">
                    <div class="discipline-list-item pa-3 rounded-lg d-flex align-center">
                      <v-icon color="#fbc02d" small class="mr-2">mdi-rowing</v-icon>
                      <div>
                        <div class="white--text font-weight-bold caption leading-tight">8 Min RowErg</div>
                        <div class="grey--text caption-xs">Distanz (m)</div>
                      </div>
                    </div>
                    <div class="discipline-list-item pa-3 rounded-lg d-flex align-center">
                      <v-icon color="#fbc02d" small class="mr-2">mdi-human-handsup</v-icon>
                      <div>
                        <div class="white--text font-weight-bold caption leading-tight">4 Min Burpees</div>
                        <div class="grey--text caption-xs">Broad Jumps (Reps)</div>
                      </div>
                    </div>
                    <div class="discipline-list-item pa-3 rounded-lg d-flex align-center">
                      <v-icon color="#fbc02d" small class="mr-2">mdi-walk</v-icon>
                      <div>
                        <div class="white--text font-weight-bold caption leading-tight">4 Min Lunges</div>
                        <div class="grey--text caption-xs">Reverse (Reps)</div>
                      </div>
                    </div>
                    <div class="discipline-list-item pa-3 rounded-lg d-flex align-center">
                      <v-icon color="#fbc02d" small class="mr-2">mdi-ski</v-icon>
                      <div>
                        <div class="white--text font-weight-bold caption leading-tight">8 Min SkiErg</div>
                        <div class="grey--text caption-xs">Distanz (m)</div>
                      </div>
                    </div>
                    <div class="discipline-list-item pa-3 rounded-lg d-flex align-center">
                      <v-icon color="#fbc02d" small class="mr-2">mdi-volleyball</v-icon>
                      <div>
                        <div class="white--text font-weight-bold caption leading-tight">4 Min Wall Balls</div>
                        <div class="grey--text caption-xs">Wiederholungen</div>
                      </div>
                    </div>
                    <div class="discipline-list-item pa-3 rounded-lg d-flex align-center">
                      <v-icon color="#fbc02d" small class="mr-2">mdi-run</v-icon>
                      <div>
                        <div class="white--text font-weight-bold caption leading-tight">5 km Run</div>
                        <div class="grey--text caption-xs">Outdoor</div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 5K Result Notice Box -->
                <div class="notice-box pa-4 rounded-xl border-highlight-gold">
                  <div class="d-flex align-center mb-3">
                    <v-icon color="#fbc02d" class="mr-2">mdi-alert-decagram-outline</v-icon>
                    <h4 class="white--text font-weight-black subtitle-2 text-uppercase tracking-wider">Wichtig vorab: Dein 5-km-Resultat</h4>
                  </div>
                  <p class="grey--text text--lighten-2 caption mb-3 leading-relaxed">
                    Für die Berechnung deines HYROX Scores benötigst du eine 5-km-Zeit aus den letzten zwei Monaten. Du hast zwei Möglichkeiten:
                  </p>

                  <div class="options-stack">
                    <div class="option-item pa-3 rounded-lg mb-2 d-flex align-start">
                      <v-icon color="green accent-3" class="mr-2 mt-0-5" small>mdi-check-circle-outline</v-icon>
                      <div class="caption grey--text text--lighten-1">
                        <strong>Möglichkeit 1:</strong> Du läufst die 5 km mit uns am 18. Juli und verwendest dieses Resultat für deinen HYROX Score.
                      </div>
                    </div>
                    <div class="option-item pa-3 rounded-lg d-flex align-start">
                      <v-icon color="green accent-3" class="mr-2 mt-0-5" small>mdi-check-circle-outline</v-icon>
                      <div class="caption grey--text text--lighten-1">
                        <strong>Möglichkeit 2:</strong> Du bringst deine eigene 5-km-Zeit mit, sofern sie innerhalb der letzten zwei Monate gelaufen
                        wurde.
                      </div>
                    </div>
                  </div>

                  <div class="notice-footer mt-3 pt-3 text-center">
                    <span class="caption yellow--text text--darken-2 font-weight-bold">
                      Bitte halte deine 5-km-Zeit bei der Anmeldung bzw. spätestens am Event bereit.
                    </span>
                  </div>
                </div>
              </div>

              <!-- Stats bar -->
              <v-row class="mb-6" no-gutters>
                <v-col cols="12" sm="6" md="3" class="pa-2">
                  <div class="info-metric pa-4 rounded-xl text-center d-flex flex-column justify-center align-center" style="height: 100%">
                    <v-icon color="#fbc02d" class="mb-1">mdi-clock-outline</v-icon>
                    <div class="caption grey--text">Briefing / Start</div>
                    <div class="font-weight-black white--text subtitle-2">08:45 / 09:00 Uhr</div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3" class="pa-2">
                  <div class="info-metric pa-4 rounded-xl text-center d-flex flex-column justify-center align-center" style="height: 100%">
                    <v-icon color="#fbc02d" class="mb-1">mdi-cellphone-link</v-icon>
                    <div class="caption grey--text">Capture App</div>
                    <div class="font-weight-black white--text subtitle-2" style="font-size: 12px; line-height: 1.2">Zugang zur Capture App</div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3" class="pa-2">
                  <div class="info-metric pa-4 rounded-xl text-center d-flex flex-column justify-center align-center" style="height: 100%">
                    <v-icon color="#fbc02d" class="mb-1">mdi-camera-outline</v-icon>
                    <div class="caption grey--text">Fotograf</div>
                    <div class="font-weight-black white--text subtitle-2" style="font-size: 13px; line-height: 1.2">Link zu den Fotos</div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3" class="pa-2">
                  <div class="info-metric pa-4 rounded-xl text-center d-flex flex-column justify-center align-center" style="height: 100%">
                    <v-icon color="#fbc02d" class="mb-1">mdi-credit-card-outline</v-icon>
                    <div class="caption grey--text">Gebühr</div>
                    <div class="font-weight-black white--text subtitle-2">CHF 20 pro Starter</div>
                  </div>
                </v-col>
              </v-row>

              <!-- Registration Mode Cards Selector -->
              <div class="registration-mode-cards mb-6">
                <div
                  class="mode-card rounded-xl pa-4 text-center cursor-pointer transition-all"
                  :class="{ 'active-mode': registrationMode === 'single' }"
                  @click="registrationMode = 'single'">
                  <v-icon size="28" :color="registrationMode === 'single' ? '#000000' : '#fbc02d'" class="mb-2"> mdi-account-outline </v-icon>
                  <div class="font-weight-black title-text">Einzelstarter/in</div>
                  <div class="caption-text mt-1">Zufälliger Judge vor Ort</div>
                </div>

                <div
                  class="mode-card rounded-xl pa-4 text-center cursor-pointer transition-all"
                  :class="{ 'active-mode': registrationMode === 'team' }"
                  @click="registrationMode = 'team'">
                  <v-icon size="28" :color="registrationMode === 'team' ? '#000000' : '#fbc02d'" class="mb-2"> mdi-account-group-outline </v-icon>
                  <div class="font-weight-black title-text">Mit Partner/in</div>
                  <div class="caption-text mt-1">Gegenseitiges Richten (Judgen)</div>
                </div>
              </div>

              <v-form ref="form" v-model="valid">
                <!-- Participant 1 (Autofilled Logged-in User) -->
                <div class="user-info-box pa-4 rounded-xl mb-6">
                  <div class="d-flex align-center mb-3">
                    <v-avatar size="24" color="#fbc02d" class="mr-2 black--text font-weight-bold">1</v-avatar>
                    <span class="font-weight-black white--text subtitle-2">
                      {{ registrationMode === "team" ? "Starter 1 (Du)" : "Deine Angaben" }}
                    </span>
                  </div>
                  <v-row dense>
                    <v-col cols="6">
                      <v-text-field
                        :value="user.vorname"
                        label="Vorname"
                        disabled
                        outlined
                        dense
                        dark
                        hide-details
                        class="custom-field"></v-text-field>
                    </v-col>
                    <v-col cols="6">
                      <v-text-field
                        :value="user.nachname"
                        label="Nachname"
                        disabled
                        outlined
                        dense
                        dark
                        hide-details
                        class="custom-field"></v-text-field>
                    </v-col>
                    <v-col cols="12" class="mt-2">
                      <v-text-field
                        :value="user.email"
                        label="E-Mail-Adresse"
                        disabled
                        outlined
                        dense
                        dark
                        hide-details
                        class="custom-field"></v-text-field>
                    </v-col>
                  </v-row>

                  <v-divider class="grey darken-3 my-3"></v-divider>

                  <v-checkbox
                    v-model="runSelf"
                    label="Ich renne den 5k selber, da ich am 18. Juli keine Zeit habe"
                    color="#fbc02d"
                    dark
                    hide-details
                    class="mt-0"></v-checkbox>

                  <v-expand-transition>
                    <div v-if="runSelf" class="mt-3">
                      <v-text-field
                        v-model="runSelfTime"
                        label="5k Run Zeit"
                        outlined
                        dense
                        dark
                        placeholder="z.B. 24:30"
                        :rules="runSelf ? [(v) => !!v || '5-km-Zeit ist erforderlich'] : []"
                        class="custom-field mb-0"
                        hide-details="auto"></v-text-field>
                    </div>
                  </v-expand-transition>
                </div>

                <!-- Participant 2 (Partner Inputs) -->
                <div v-if="registrationMode === 'team'" class="user-info-box pa-4 rounded-xl border-highlight mb-6">
                  <div class="d-flex align-center mb-3">
                    <v-avatar size="24" color="#fbc02d" class="mr-2 black--text font-weight-bold">2</v-avatar>
                    <span class="font-weight-black white--text subtitle-2">Starter 2 (Partner/gegenseitiger Judge)</span>
                  </div>

                  <v-alert type="info" outlined dense color="#fbc02d" class="caption mb-4 dark-alert">
                    Die E-Mail-Adresse deines Partners muss mit seinem Well Come Fit Mitgliederkonto übereinstimmen. Ihr richtet euch beim Event
                    gegenseitig.
                  </v-alert>

                  <v-row dense>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="partner.firstName"
                        label="Vorname*"
                        required
                        :rules="[(v) => !!v || 'Vorname ist erforderlich']"
                        outlined
                        dense
                        dark
                        class="custom-field"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="partner.lastName"
                        label="Nachname*"
                        required
                        :rules="[(v) => !!v || 'Nachname ist erforderlich']"
                        outlined
                        dense
                        dark
                        class="custom-field"></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="partner.email"
                        label="E-Mail-Adresse*"
                        required
                        :rules="[(v) => !!v || 'E-Mail ist erforderlich', (v) => /.+@.+\..+/.test(v) || 'Ungültige E-Mail-Adresse']"
                        outlined
                        dense
                        dark
                        class="custom-field"></v-text-field>
                    </v-col>
                  </v-row>

                  <v-divider class="grey darken-3 my-3"></v-divider>

                  <v-checkbox
                    v-model="partner.runSelf"
                    label="Ich renne den 5k selber, da ich am 18. Juli keine Zeit habe"
                    color="#fbc02d"
                    dark
                    hide-details
                    class="mt-0"></v-checkbox>

                  <v-expand-transition>
                    <div v-if="partner.runSelf" class="mt-3">
                      <v-text-field
                        v-model="partner.runSelfTime"
                        label="5k Run Zeit"
                        outlined
                        dense
                        dark
                        placeholder="z.B. 24:30"
                        :rules="partner.runSelf ? [(v) => !!v || '5-km-Zeit ist erforderlich'] : []"
                        class="custom-field mb-0"
                        hide-details="auto"></v-text-field>
                    </div>
                  </v-expand-transition>
                </div>

                <!-- Next button -->
                <v-btn
                  color="#fbc02d"
                  block
                  large
                  class="black--text font-weight-black rounded-lg py-6"
                  :disabled="!valid"
                  @click="submitRegistrationCheck">
                  Weiter zur Überprüfung
                </v-btn>
              </v-form>
            </v-window-item>

            <!-- STEP 2: Validation Loader / Price Summary -->
            <v-window-item :value="2">
              <div v-if="verifying" class="text-center py-12">
                <v-progress-circular indeterminate color="#fbc02d" size="50"></v-progress-circular>
                <div class="white--text font-weight-bold mt-4 subtitle-1">Überprüfe Mitgliedschaften & Berechtigungen...</div>
                <div class="grey--text caption mt-2">Wir gleichen die Profile mit dem System ab.</div>
              </div>

              <div v-else-if="verificationError" class="text-center py-6">
                <v-icon size="64" color="red" class="mb-4">mdi-alert-circle-outline</v-icon>
                <h3 class="text-h6 font-weight-bold white--text mb-2">Prüfung fehlgeschlagen</h3>
                <p class="red--text text--lighten-2 body-2 px-4 max-width-text mx-auto" v-html="verificationError"></p>
                <v-btn text color="grey" class="mt-4" @click="step = 1"> Zurück zur Eingabe </v-btn>
              </div>

              <div v-else>
                <h3 class="subtitle-1 font-weight-black white--text mb-4 text-center">Registrierungs-Zusammenfassung</h3>

                <div class="summary-box pa-4 rounded-xl mb-6">
                  <!-- User -->
                  <div class="py-2" :class="{ 'border-bottom': registrationMode === 'team' }">
                    <div class="d-flex justify-space-between align-center">
                      <span class="grey--text">{{ registrationMode === "team" ? "Starter 1 (Du):" : "Anmelder (Du):" }}</span>
                      <span class="white--text font-weight-bold">
                        {{ user.vorname }} {{ user.nachname }}
                        <v-chip x-small color="green" class="ml-1 black--text font-weight-black" v-if="userIsInstructor">KURSLEITER</v-chip>
                        <v-chip x-small outlined color="grey" class="ml-1" v-else>MITGLIED</v-chip>
                      </span>
                    </div>
                    <div v-if="runSelf" class="text-right mt-1">
                      <v-chip x-small color="#fbc02d" class="black--text font-weight-bold">Renne 5k selber (Zeit: {{ runSelfTime }})</v-chip>
                    </div>
                  </div>

                  <!-- Partner (Only for Team Mode) -->
                  <div v-if="registrationMode === 'team'" class="py-2">
                    <div class="d-flex justify-space-between align-center">
                      <span class="grey--text">Starter 2 (Partner/Judge):</span>
                      <span class="white--text font-weight-bold">
                        {{ partner.firstName }} {{ partner.lastName }}
                        <v-chip x-small color="green" class="ml-1 black--text font-weight-black" v-if="partnerIsInstructor">KURSLEITER</v-chip>
                        <v-chip x-small outlined color="grey" class="ml-1" v-else>MITGLIED</v-chip>
                      </span>
                    </div>
                    <div v-if="partner.runSelf" class="text-right mt-1">
                      <v-chip x-small color="#fbc02d" class="black--text font-weight-bold">Renne 5k selber (Zeit: {{ partner.runSelfTime }})</v-chip>
                    </div>
                  </div>

                  <v-divider class="grey darken-3 my-2"></v-divider>

                  <div class="d-flex justify-space-between align-center py-2 pt-2">
                    <span class="white--text font-weight-bold">Gesamtbetrag:</span>
                    <span class="yellow--text text--darken-2 font-weight-black text-h6"> CHF {{ totalFee.toFixed(2) }} </span>
                  </div>
                </div>

                <div class="d-flex flex-column flex-sm-row justify-space-between gap-4 mt-6">
                  <v-btn text color="grey" large class="rounded-lg" @click="step = 1"> Zurück </v-btn>
                  <v-btn color="#fbc02d" class="black--text font-weight-bold rounded-lg px-6" large :loading="registering" @click="proceedToPayment">
                    {{ totalFee > 0 ? "Weiter zur Zahlung" : "Jetzt kostenlos anmelden" }}
                  </v-btn>
                </div>
              </div>
            </v-window-item>

            <!-- STEP 3: Adyen Credit Card Payment Flow -->
            <v-window-item :value="3">
              <h3 class="subtitle-1 font-weight-black white--text mb-4 text-center">Zahlung</h3>

              <!-- Payment Summary -->
              <div v-show="!paymentLoading && !paymentVerifying" class="payment-summary text-center mb-6">
                <div class="grey--text subtitle-2 mb-1">Teilnahmegebühr</div>
                <div class="yellow--text text--darken-2 font-weight-black text-h4">CHF {{ totalFee.toFixed(2) }}</div>
              </div>

              <!-- Loading SDK Loader -->
              <div v-if="paymentLoading" class="text-center py-12">
                <v-progress-circular indeterminate color="#fbc02d" size="50"></v-progress-circular>
                <div class="white--text font-weight-bold mt-4 subtitle-1">Zahlungsmodul wird geladen...</div>
                <div class="grey--text caption mt-2">Bitte gedulde dich einen Moment.</div>
              </div>

              <!-- Payment Verification Loader -->
              <div v-if="paymentVerifying" class="text-center py-12">
                <v-progress-circular indeterminate color="#fbc02d" size="50"></v-progress-circular>
                <div class="white--text font-weight-bold mt-4 subtitle-1">Deine Zahlung wird verifiziert...</div>
                <div class="grey--text caption mt-2">Bitte lade die Seite nicht neu und schliesse das Fenster nicht.</div>
              </div>

              <!-- Adyen Drop-in Mount Container -->
              <div
                v-show="!paymentLoading && !paymentVerifying"
                id="adyen-dropin-container"
                class="mt-4 pa-4 rounded-xl"
                style="background-color: rgba(255, 255, 255, 0.02); border: 1px solid rgba(255, 255, 255, 0.05)"></div>

              <div v-if="!paymentLoading && !paymentVerifying" class="d-flex justify-start gap-4 mt-6">
                <v-btn text color="grey" large class="rounded-lg" @click="step = 1"> Abbrechen </v-btn>
              </div>
            </v-window-item>

            <!-- STEP 4: Success Screen with 5k Run Promos -->
            <v-window-item :value="4">
              <div class="text-center py-6">
                <v-icon size="80" color="green" class="mb-4 animate-bounce">mdi-check-circle-outline</v-icon>
                <h2 class="text-h5 font-weight-black white--text mb-2">ANMELDUNG ERFOLGREICH!</h2>
                <p class="grey--text text--lighten-1 body-2 max-width-text mx-auto mb-6">
                  Vielen Dank für deine Anmeldung zum HYROX Score Event. Wir haben eine Anmeldebestätigung an dich
                  {{ registrationMode === "team" ? "und deinen Partner/gegenseitigen Judge" : "" }} per E-Mail gesendet.
                </p>

                <v-card color="rgba(255, 255, 255, 0.03)" class="rounded-xl border-highlight pa-5 text-left mb-6 mx-auto" style="max-width: 500px">
                  <h3 class="subtitle-2 font-weight-black yellow--text text--darken-2 text-uppercase mb-3 d-flex align-center">
                    <v-icon color="#fbc02d" small class="mr-2">mdi-run</v-icon>
                    Du willst deinen 5k Run ebenfalls mit uns machen?
                  </h3>
                  <p class="caption grey--text mb-4">
                    Melde dich direkt zu den passenden Gruppenkursen an (für Event-Teilnehmer kostenlos / gratis):
                  </p>

                  <div class="d-flex flex-column gap-3">
                    <v-btn @click="openCourseDialog('frauenfeld')" block outlined color="#fbc02d" class="text-none font-weight-bold py-5 rounded-lg">
                      5k Run am 18. Juli in Frauenfeld
                    </v-btn>
                    <v-btn @click="openCourseDialog('stgallen')" block outlined color="#fbc02d" class="text-none font-weight-bold py-5 rounded-lg">
                      5k Run am 18. Juli in St. Gallen
                    </v-btn>
                  </div>
                </v-card>

                <div class="info-alert pa-4 rounded-xl max-width-text mx-auto text-left mb-6">
                  <v-icon small color="info" class="mr-2">mdi-information-outline</v-icon>
                  <span class="caption grey--text text--lighten-1">
                    Weitere Informationen sowie die Zugänge zur HYROX-App und zur Capture-App erhältst du nach Anmeldeschluss per E-Mail.
                  </span>
                </div>

                <v-btn color="#fbc02d" class="black--text font-weight-black rounded-lg px-8" large @click="goHome"> Fertigstellen </v-btn>
              </div>
            </v-window-item>
          </v-window>
        </div>
      </v-card>
    </v-container>

    <!-- Dialog for Group Fitness Course Registration -->
    <v-dialog v-model="courseDialog.show" max-width="500px" overlay-color="#000" class="rounded-xl">
      <v-card
        dark
        class="pa-4 rounded-xl border-highlight animate-fade-in"
        style="background-color: #121212; border: 1px solid rgba(255, 255, 255, 0.08)">
        <div v-if="courseDialog.loading" class="text-center py-8">
          <v-progress-circular indeterminate color="#fbc02d" size="50"></v-progress-circular>
          <div class="caption grey--text mt-3">Kurs-Details werden geladen...</div>
        </div>
        <div v-else-if="!courseDialog.courseView" class="text-center py-6">
          <v-icon color="error" size="48">mdi-alert-circle-outline</v-icon>
          <div class="subtitle-1 white--text mt-3">Details konnten nicht geladen werden.</div>
          <v-btn text color="grey" class="mt-4" @click="courseDialog.show = false">Schliessen</v-btn>
        </div>
        <div v-else>
          <!-- Title -->
          <div class="d-flex align-start justify-space-between mb-4">
            <div>
              <div class="caption yellow--text text--darken-2 text-uppercase font-weight-black tracking-wider" style="letter-spacing: 1px">
                5K Run Vorbereitung
              </div>
              <h2 class="text-h5 font-weight-black white--text italic-title">{{ courseDialog.courseView.course.name }}</h2>
            </div>
            <v-btn icon color="grey" @click="courseDialog.show = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <!-- Banner / Image (if any) -->
          <v-img
            v-if="courseDialog.courseView.course.bild"
            :src="'/img/kurse/' + courseDialog.courseView.course.bild"
            class="rounded-lg mb-4"
            height="150"
            cover>
          </v-img>

          <!-- Quick Details Grid -->
          <v-row dense class="mb-4">
            <v-col cols="6">
              <div
                class="d-flex align-center pa-2 rounded-lg"
                style="background-color: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.05)">
                <v-icon color="#fbc02d" class="mr-2">mdi-clock-outline</v-icon>
                <div>
                  <div class="caption grey--text">Zeit</div>
                  <div class="body-2 font-weight-bold white--text">
                    {{ formatTime(courseDialog.courseView.hours, courseDialog.courseView.minutes) }} Uhr
                  </div>
                </div>
              </div>
            </v-col>
            <v-col cols="6">
              <div
                class="d-flex align-center pa-2 rounded-lg"
                style="background-color: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.05)">
                <v-icon color="#fbc02d" class="mr-2">mdi-map-marker-outline</v-icon>
                <div>
                  <div class="caption grey--text">Ort</div>
                  <div class="body-2 font-weight-bold white--text" style="font-size: 13px; line-height: 1.2">
                    {{ courseDialog.courseView.room ? courseDialog.courseView.room.name : "Studio" }}
                    <span class="caption grey--text d-block" style="font-size: 10px">{{ courseDialog.courseView.studio.name }}</span>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- Description -->
          <div class="mb-4" v-if="courseDialog.courseView.course.beschreibung">
            <div class="caption grey--text text-uppercase font-weight-bold mb-1">Beschreibung</div>
            <div class="body-2 grey--text text--lighten-2" style="line-height: 1.4; max-height: 100px; overflow-y: auto">
              {{ courseDialog.courseView.course.beschreibung }}
            </div>
          </div>

          <!-- Instructors -->
          <div class="mb-4" v-if="courseDialog.courseView.instructors && courseDialog.courseView.instructors.length">
            <div class="caption grey--text text-uppercase font-weight-bold mb-2">Instruktor</div>
            <div
              class="d-flex align-center pa-2 rounded-lg"
              style="background-color: rgba(255, 255, 255, 0.02); border: 1px solid rgba(255, 255, 255, 0.05)"
              v-for="(inst, i) in courseDialog.courseView.instructors"
              :key="i">
              <v-avatar size="32" class="mr-2">
                <v-img
                  :src="
                    inst.bild
                      ? '/img/profilbilder/' + inst.bild
                      : 'https://media.istockphoto.com/id/1337144146/de/vektor/standard-avatar-profilsymbolvektor.jpg?s=612x612&w=0&k=20&c=rw3jpsQAdTti6Hh9SAGN15Ex8HYYwmHv3JFHS6KStMo='
                  "></v-img>
              </v-avatar>
              <div>
                <div class="body-2 font-weight-bold white--text">{{ inst.vorname }} {{ inst.nachname }}</div>
                <div class="caption grey--text" style="font-size: 10px">Kursleiter{{ inst.geschlecht === "w" ? "in" : "" }}</div>
              </div>
            </div>
          </div>

          <!-- Capacity Indicator -->
          <div
            class="d-flex align-center justify-space-between mb-6 pa-3 rounded-lg"
            style="background-color: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.05)">
            <div class="d-flex align-center">
              <v-icon color="#fbc02d" class="mr-2">mdi-account-group-outline</v-icon>
              <div>
                <div class="caption grey--text">Teilnehmer</div>
                <div class="body-2 font-weight-bold white--text">
                  {{ courseDialog.courseView.signups ? courseDialog.courseView.signups.length : 0 }} / {{ courseDialog.courseView.amount }} Plätze
                </div>
              </div>
            </div>
            <div>
              <v-chip x-small v-if="courseDialog.signedUp" color="green" class="black--text font-weight-black">ANGEMELDET</v-chip>
              <v-chip x-small v-else-if="courseDialog.onQueue" color="warning" class="black--text font-weight-black">WARTELISTE</v-chip>
              <v-chip
                x-small
                v-else-if="courseDialog.courseView.signups && courseDialog.courseView.signups.length >= courseDialog.courseView.amount"
                color="orange"
                class="black--text font-weight-bold"
                >WARTELISTE AKTIV</v-chip
              >
              <v-chip x-small v-else color="green" outlined class="font-weight-bold">PLÄTZE FREI</v-chip>
            </div>
          </div>

          <!-- Dialog Actions / Book button -->
          <div class="d-flex flex-column gap-2">
            <!-- Main button -->
            <v-btn
              block
              large
              :color="courseDialog.signedUp ? 'red darken-2' : '#fbc02d'"
              :class="courseDialog.signedUp ? 'white--text' : 'black--text'"
              class="font-weight-black rounded-lg"
              :loading="courseDialog.actionLoading"
              @click="toggleCourseSignup">
              {{ getCourseButtonText() }}
            </v-btn>

            <v-btn block text color="grey" @click="courseDialog.show = false"> Schliessen </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import { mapState } from "vuex"

export default {
  name: "HyroxScoreRegistration",
  data() {
    return {
      step: 1,
      loading: true,
      valid: false,
      verifying: false,
      registering: false,
      isClosed: false,
      isSoldOut: false,
      verificationError: null,
      deadlineDate: "",

      // Mode Selection: 'team' or 'single'
      registrationMode: "single",

      // Form Inputs
      runSelf: false,
      runSelfTime: "",
      partner: {
        firstName: "",
        lastName: "",
        email: "",
        runSelf: false,
        runSelfTime: "",
      },

      // Computed backend check properties
      userIsInstructor: false,
      partnerIsInstructor: false,
      totalFee: 40,
      registrationId: null,
      checkoutConfig: null,
      transactionId: null,
      paymentLoading: false,
      paymentVerifying: false,
      checkout: null,
      adyenDropin: null,

      // Group Fitness integration properties
      courseFrauenfeldId: "",
      courseStGallenId: "",
      courseDialog: {
        show: false,
        loading: false,
        actionLoading: false,
        courseView: null,
        signedUp: false,
        onQueue: false,
      },
    }
  },
  computed: {
    ...mapState("userStore", ["user"]),
  },
  async created() {
    await this.fetchEventAvailability()
    const query = this.$route.query
    if (query.status === "checkPayment" && query.txId) {
      this.step = 3
      this.paymentVerifying = true
      this.verifyAdyenPayment(query.txId, query.redirectResult)
    }
  },
  methods: {
    goBack() {
      this.$router.go(-1)
    },
    goHome() {
      this.$router.push("/home")
    },
    async fetchEventAvailability() {
      try {
        const stats = await api.hyroxScore.fetchAvailability()
        this.isClosed = stats.isClosed
        this.isSoldOut = stats.activeParticipants >= stats.limit
        this.courseFrauenfeldId = stats.courseFrauenfeldId || ""
        this.courseStGallenId = stats.courseStGallenId || ""
        this.deadlineDate = stats.deadlineDate || ""

        // If user already signed up, route them directly to confirmation if active
        if (stats.myRegistration) {
          this.step = 4
          if (stats.myRegistration.partner && stats.myRegistration.partner.email) {
            this.registrationMode = "team"
          } else {
            this.registrationMode = "single"
          }
        }
      } catch (e) {
        console.error("Failed to load hyrox score stats", e)
      } finally {
        this.loading = false
      }
    },
    formatTime(hours, minutes) {
      if (hours === undefined || minutes === undefined) return ""
      const hStr = String(hours).padStart(2, "0")
      const mStr = String(minutes).padStart(2, "0")
      return `${hStr}:${mStr}`
    },
    formatDeadlineDate(dateStr) {
      if (!dateStr) return ""
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return ""
      // We subtract 1 second to get the last moment registrations were open
      const endOpen = new Date(d.getTime() - 1000)

      const day = endOpen.getDate()
      const months = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"]
      const monthStr = months[endOpen.getMonth()]

      return `${day}. ${monthStr}`
    },
    openCourseDialog(city) {
      const courseId = city === "frauenfeld" ? this.courseFrauenfeldId : this.courseStGallenId
      if (!courseId) {
        this.$toast.info("Dieser Kurs ist aktuell nicht für Online-Buchungen freigegeben. Bitte wende dich an das Studio.")
        return
      }
      this.courseDialog.show = true
      this.loadCourseDetails(courseId)
    },
    async loadCourseDetails(courseId) {
      this.courseDialog.loading = true
      try {
        const courseData = await api.fetchCourseView(courseId, 0)
        this.courseDialog.courseView = courseData.courseView
        this.courseDialog.signedUp = courseData.signedUp
        this.courseDialog.onQueue = courseData.onQueue
      } catch (e) {
        console.error("Failed to load course details", e)
        this.$toast.error("Details konnten nicht geladen werden.")
        this.courseDialog.show = false
      } finally {
        this.courseDialog.loading = false
      }
    },
    async toggleCourseSignup() {
      if (!this.courseDialog.courseView) return
      this.courseDialog.actionLoading = true
      try {
        const courseId = this.courseDialog.courseView._id
        const newStatus = !this.courseDialog.signedUp

        const response = await api.manageCourseSignup(courseId, newStatus, "")

        if (response.simulatenousSignup) {
          this.$toast.error("Du kannst dich nicht für mehrere Kurse gleichzeitig anmelden.")
        } else if (response.toManyEventSignups) {
          this.$toast.error("Du hast dich bereits für zu viele Kurse dieses Events angemeldet.")
        } else {
          this.$toast.success(newStatus ? "Erfolgreich angemeldet!" : "Erfolgreich abgemeldet.")
        }

        await this.loadCourseDetails(courseId)
      } catch (e) {
        console.error("Course signup error", e)
        this.$toast.error("Aktion fehlgeschlagen. Bitte versuche es später erneut.")
      } finally {
        this.courseDialog.actionLoading = false
      }
    },
    getCourseButtonText() {
      if (!this.courseDialog.courseView) return ""
      if (this.courseDialog.signedUp) return "Abmelden"

      const isFull = this.courseDialog.courseView.signups && this.courseDialog.courseView.signups.length >= this.courseDialog.courseView.amount

      return isFull ? "In Warteliste eintragen" : "Kostenlos anmelden"
    },
    async submitRegistrationCheck() {
      if (!this.$refs.form.validate()) return

      this.step = 2
      this.verifying = true
      this.verificationError = null

      try {
        const payload = {
          partnerFirstName: "",
          partnerLastName: "",
          partnerEmail: "",
          runSelf: this.runSelf,
          runSelfTime: this.runSelfTime,
          partnerRunSelf: false,
          partnerRunSelfTime: "",
          returnUrl: window.location.origin + `/hyrox-score?status=checkPayment`,
        }

        if (this.registrationMode === "team") {
          payload.partnerFirstName = this.partner.firstName
          payload.partnerLastName = this.partner.lastName
          payload.partnerEmail = this.partner.email
          payload.partnerRunSelf = this.partner.runSelf
          payload.partnerRunSelfTime = this.partner.runSelfTime
        }

        const res = await api.hyroxScore.register(payload)
        this.registrationId = res.registrationId
        this.totalFee = res.totalFee

        if (res.checkoutConfig) {
          this.checkoutConfig = res.checkoutConfig
          this.transactionId = res.transactionId
        }

        // Set tag highlights depending on registration fee
        if (this.totalFee === 0) {
          this.userIsInstructor = true
          this.partnerIsInstructor = true
        } else {
          this.userIsInstructor = false
          this.partnerIsInstructor = false
        }

        this.verifying = false
        if (this.totalFee === 0) {
          this.step = 4 // Direct success
        } else {
          this.step = 3 // Go to payment
          this.$nextTick(() => {
            this.initAdyenCheckout()
          })
        }
      } catch (error) {
        this.verifying = false
        const msg = error.response?.data || "Überprüfung fehlgeschlagen. Bitte vergewissere dich, dass eine aktive Mitgliedschaft besteht."
        this.verificationError = msg
      }
    },
    async initAdyenCheckout() {
      // NEU: Dem Flutter-Wrapper erlauben, die externen Bank-Redirects von Adyen in der App zu behalten
      if (window.flutter_inappwebview) {
        window.flutter_inappwebview.callHandler("allowAllRedirects")
      }
      // Reset any previous checkout instance to prevent session caching issues
      if (this.adyenDropin) {
        try {
          this.adyenDropin.unmount()
        } catch (e) {
          console.error("Failed to unmount Adyen drop-in", e)
        }
        this.adyenDropin = null
      }
      this.checkout = null
      const container = document.getElementById("adyen-dropin-container")
      if (container) {
        container.innerHTML = ""
      }

      this.paymentLoading = true

      // Ensure CSS is loaded
      if (!document.getElementById("adyen-css")) {
        const link = document.createElement("link")
        link.id = "adyen-css"
        link.rel = "stylesheet"
        link.href = "https://checkoutshopper-live.adyen.com/checkoutshopper/sdk/v5.57.0/adyen.css"
        document.head.appendChild(link)
      }

      try {
        const AdyenCheckout = (await import("@adyen/adyen-web")).default

        const returnUrl = window.location.origin + `/hyrox-score?status=checkPayment&txId=${this.transactionId}`

        const configuration = {
          environment: this.checkoutConfig.environment || "live",
          clientKey: this.checkoutConfig.clientKey,
          session: this.checkoutConfig.session,
          onPaymentCompleted: async (result, component) => {
            console.log("Adyen onPaymentCompleted:", result)
            if (["Authorised", "Received", "Pending"].includes(result.resultCode)) {
              await this.verifyAdyenPayment(this.transactionId, null, result.pspReference)
            } else {
              this.$toast.error("Zahlung fehlgeschlagen. Bitte versuche es erneut.")
            }
          },
          onError: (error, component) => {
            console.error("Adyen onError:", error)
            this.$toast.error("Zahlung abgebrochen oder fehlgeschlagen.")
          },
          paymentMethodsConfiguration: {
            card: {
              hasHolderName: true,
              holderNameRequired: true,
              billingAddressRequired: false,
            },
          },
        }

        this.checkout = await AdyenCheckout(configuration)
        this.adyenDropin = this.checkout.create("dropin").mount("#adyen-dropin-container")
      } catch (err) {
        console.error("Failed to init Adyen checkout:", err)
        this.$toast.error("Zahlungsmodul konnte nicht geladen werden.")
      } finally {
        this.paymentLoading = false
      }
    },
    async verifyAdyenPayment(transactionId, redirectResult = null, pspReference = null) {
      this.paymentVerifying = true
      let retryCount = 0
      const maxRetries = 30 // up to 75 seconds total polling

      try {
        let response
        do {
          response = await api.hyroxScore.payment.verify(transactionId, redirectResult, pspReference)

          if (response === "webhookMissing") {
            if (retryCount >= maxRetries) {
              this.$toast.error("Verifizierung überschritten. Bitte wende dich an den Support.")
              this.paymentVerifying = false
              return
            }
            retryCount++
            // Wait 2.5 seconds before retrying
            await new Promise((resolve) => setTimeout(resolve, 2500))
          }
        } while (response === "webhookMissing")

        if (response.status === "captured") {
          this.$toast.success("Zahlung erfolgreich!")
          this.step = 4 // Success screen
        } else {
          this.$toast.error(response.message || "Zahlung fehlgeschlagen.")
          this.step = 3 // Go back to payment input
          if (this.adyenDropin) {
            this.adyenDropin.setStatus("ready")
          }
        }
      } catch (error) {
        console.error("Payment verification failed:", error)
        this.$toast.error("Zahlungsverifizierung fehlgeschlagen.")
        this.step = 3
        if (this.adyenDropin) {
          this.adyenDropin.setStatus("ready")
        }
      } finally {
        this.paymentVerifying = false
      }
    },
  },
}
</script>

<style scoped>
.hyrox-score-shell {
  background-color: #0f0f0f;
  min-height: 100vh;
  color: white;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.back-btn {
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.hyrox-score-container {
  max-width: 650px;
  margin: 0 auto;
}

.closed-container {
  background-color: #1c1c1e;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px;
  padding: 40px 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}

.max-width-text {
  max-width: 480px;
}

.warning-box {
  background-color: rgba(245, 0, 87, 0.08);
  border: 1px solid rgba(245, 0, 87, 0.2);
  color: #f50057;
  font-weight: 500;
  font-size: 0.9rem;
}

/* Card Styling */
.hyrox-score-card {
  position: relative;
  background-color: #1c1c1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  overflow: hidden;
}

.card-glow {
  position: absolute;
  top: -150px;
  left: 50%;
  transform: translateX(-50%);
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(251, 192, 45, 0.15) 0%, transparent 70%);
  filter: blur(40px);
  pointer-events: none;
}

.relative-content {
  position: relative;
  z-index: 2;
}

/* Premium Info Block Styling */
.premium-info-block {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.02) 0%, rgba(255, 255, 255, 0) 100%);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 24px;
  padding: 24px;
}

.yellow-accent-bar {
  display: inline-block;
  width: 4px;
  height: 36px;
  background-color: #fbc02d;
  border-radius: 2px;
}

.disciplines-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.discipline-list-item {
  background-color: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.2s ease;
}

.discipline-list-item:hover {
  background-color: rgba(255, 255, 255, 0.04);
  border-color: rgba(251, 192, 45, 0.2);
}

.disciplines-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

@media (max-width: 480px) {
  .disciplines-grid {
    grid-template-columns: 1fr;
  }
}

.discipline-tag {
  background-color: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.2s ease;
}

.discipline-tag:hover {
  background-color: rgba(255, 255, 255, 0.04);
  border-color: rgba(251, 192, 45, 0.2);
}

.caption-xs {
  font-size: 10px;
  letter-spacing: 0.3px;
}

.notice-box {
  background: linear-gradient(135deg, rgba(251, 192, 45, 0.05) 0%, rgba(0, 0, 0, 0.2) 100%);
  border: 1px solid rgba(251, 192, 45, 0.15) !important;
}

.border-highlight-gold {
  border: 1px solid rgba(251, 192, 45, 0.25) !important;
}

.options-stack .option-item {
  background-color: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.mt-0-5 {
  margin-top: 2px;
}

.notice-footer {
  border-top: 1px dashed rgba(255, 255, 255, 0.08);
}

.leading-relaxed {
  line-height: 1.6;
}

/* Registration Mode Cards Selector */
.registration-mode-cards {
  display: flex;
  gap: 16px;
  width: 100%;
}

@media (max-width: 600px) {
  .registration-mode-cards {
    flex-direction: column;
  }
}

.mode-card {
  flex: 1;
  background-color: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.mode-card:hover {
  background-color: rgba(255, 255, 255, 0.05);
  border-color: rgba(251, 192, 45, 0.3);
  transform: translateY(-2px);
}

.mode-card.active-mode {
  background-color: #fbc02d;
  color: #000000;
  border-color: #fbc02d;
  box-shadow: 0 8px 24px rgba(251, 192, 45, 0.25);
}

.mode-card .title-text {
  font-size: 1.05rem;
  letter-spacing: 0.5px;
  color: inherit;
}

.mode-card.active-mode .title-text {
  color: #000000;
}

.mode-card:not(.active-mode) .title-text {
  color: #ffffff;
}

.mode-card .caption-text {
  font-size: 0.8rem;
  opacity: 0.8;
  color: inherit;
}

/* Tags & Headlines */
.event-tag {
  display: inline-block;
  background-color: rgba(251, 192, 45, 0.1);
  color: #fbc02d;
  font-weight: 900;
  font-size: 11px;
  letter-spacing: 2px;
  padding: 4px 12px;
  border-radius: 30px;
  border: 1px solid rgba(251, 192, 45, 0.2);
}

.event-headline {
  font-size: clamp(1.8rem, 4vw, 2.4rem);
  font-weight: 950;
  font-style: italic;
  letter-spacing: -1px;
  line-height: 1.1;
  color: #ffffff;
}

.event-subheadline {
  font-size: 1.1rem;
  font-weight: 800;
  letter-spacing: 3px;
  opacity: 0.9;
}

.divider {
  width: 60px;
  height: 3px;
  background-color: #fbc02d;
  border-radius: 2px;
}

/* Metrics & Boxes */
.info-metric {
  background-color: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.user-info-box {
  background-color: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.border-highlight {
  border: 1px solid rgba(251, 192, 45, 0.2) !important;
}

.dark-alert {
  background-color: rgba(251, 192, 45, 0.05) !important;
  color: #fbc02d !important;
}

.custom-field :deep(.v-input__control) {
  border-radius: 8px !important;
}

.summary-box {
  background-color: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.border-bottom {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.payment-box {
  background: linear-gradient(135deg, rgba(251, 192, 45, 0.05) 0%, rgba(0, 0, 0, 0.3) 100%);
  border: 1px solid rgba(251, 192, 45, 0.1);
}

.info-alert {
  background-color: rgba(29, 155, 240, 0.05);
  border: 1px solid rgba(29, 155, 240, 0.15);
}

.gap-3 {
  gap: 12px;
}
.gap-4 {
  gap: 16px;
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}
.animate-bounce {
  animation: bounce 2s infinite ease-in-out;
}

/* Adyen Dialog overrides */
::v-deep .adyen-checkout__payment-methods-list {
  padding: 0 !important;
}

::v-deep .adyen-checkout__button__text {
  color: white;
}

::v-deep .adyen-checkout__card__brands__brand-wrapper {
  line-height: 1;
}

::v-deep .adyen-checkout__input-wrapper iframe {
  max-height: 40px !important;
  border: none !important;
  padding: 0 !important;
}

::v-deep .adyen-checkout__input-wrapper {
  height: auto !important;
}
</style>
